import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config/api";

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: {
    scripts: number;
  };
  scripts?: Array<{
    id: string;
    name: string;
    websiteUrl: string;
    isActive: boolean;
    createdAt: string;
  }>;
}

const AdminCategories: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState<string>("all");

  const [formData, setFormData] = useState<Partial<Category>>({
    name: "",
    description: "",
    icon: "",
    color: "#3B82F6",
    sortOrder: 0,
    isActive: true,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchCategories();
  }, [pagination.page, searchTerm, filterActive]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(filterActive !== "all" && { isActive: filterActive }),
      });

      const response = await fetch(`${API_BASE_URL}/admin/categories?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
        }));
        setError("");
      } else {
        setError(data.message || "Failed to fetch categories");
      }
    } catch (err: any) {
      console.error("Fetch categories error:", err);
      setError(err.message || "Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = async (categoryId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSelectedCategory(data.data);
        setFormData(data.data);
        setIsEditing(false);
        setError("");
      } else {
        setError(data.message || "Failed to load category details");
      }
    } catch (err: any) {
      console.error("Select category error:", err);
      setError(err.message || "Failed to load category details");
    }
  };

  const handleCreateNew = () => {
    setSelectedCategory(null);
    setFormData({
      name: "",
      description: "",
      icon: "",
      color: "#3B82F6",
      sortOrder: 0,
      isActive: true,
    });
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Basic validation
      if (!formData.name?.trim()) {
        setError("Name is required");
        return;
      }

      const token = localStorage.getItem("adminToken");
      const url = isCreating
        ? `${API_BASE_URL}/admin/categories`
        : `${API_BASE_URL}/admin/categories/${selectedCategory?.id}`;

      const method = isCreating ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchCategories();
        setIsEditing(false);
        setIsCreating(false);
        setSelectedCategory(data.category);
        setError("");
        setSuccessMessage(
          isCreating ? "Category created successfully!" : "Category updated successfully!"
        );
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(data.message || "Failed to save category");
      }
    } catch (err: any) {
      console.error("Save category error:", err);
      setError(err.message || "Failed to save category");
    }
  };

  const handleToggleStatus = async (categoryId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/categories/${categoryId}/toggle`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchCategories();
        if (selectedCategory?.id === categoryId) {
          setSelectedCategory(data.category);
        }
        setSuccessMessage(data.message);
        setTimeout(() => setSuccessMessage(""), 3000);
        setError("");
      } else {
        setError(data.message || "Failed to toggle category status");
      }
    } catch (err: any) {
      console.error("Toggle status error:", err);
      setError(err.message || "Failed to toggle category status");
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this category? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchCategories();
        if (selectedCategory?.id === categoryId) {
          setSelectedCategory(null);
        }
        setSuccessMessage("Category deleted successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        setError("");
      } else {
        setError(data.message || "Failed to delete category");
      }
    } catch (err: any) {
      console.error("Delete category error:", err);
      setError(err.message || "Failed to delete category");
    }
  };

  const filteredCategories = categories;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Categories Management</h1>
              <p className="text-gray-600">Manage system categories and their settings</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCreateNew}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Create New Category
              </button>
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {(error || successMessage) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-red-700">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setError("")}
                    className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-green-700">{successMessage}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setSuccessMessage("")}
                    className="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Categories List */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Categories</h3>

                {/* Search and Filter */}
                <div className="space-y-4 mb-4">
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Categories</option>
                    <option value="true">Active Only</option>
                    <option value="false">Inactive Only</option>
                  </select>
                </div>

                <div className="space-y-2">
                  {filteredCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedCategory?.id === category.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleSelectCategory(category.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            {category.icon && (
                              <span className="text-lg">{category.icon}</span>
                            )}
                            <h4 className="font-medium text-gray-900">{category.name}</h4>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{category.slug}</p>
                          {category.description && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                              {category.description}
                            </p>
                          )}
                          <div className="flex items-center mt-2 space-x-4">
                            <span className="text-xs text-gray-400">
                              Scripts: {category._count?.scripts || 0}
                            </span>
                            <span className="text-xs text-gray-400">
                              Order: {category.sortOrder}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              category.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {category.isActive ? "Active" : "Inactive"}
                          </span>
                          {category.color && (
                            <div
                              className="w-4 h-4 rounded-full border border-gray-300"
                              style={{ backgroundColor: category.color }}
                            ></div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {categories.length} of {pagination.total} categories
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-600">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Category Details */}
          <div className="lg:col-span-2">
            {selectedCategory || isCreating ? (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-medium text-gray-900">
                      {isCreating ? "Create New Category" : `${selectedCategory?.name} Details`}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {!isCreating && selectedCategory && (
                        <>
                          <button
                            onClick={() => handleToggleStatus(selectedCategory.id)}
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              selectedCategory.isActive
                                ? "bg-red-100 text-red-800 hover:bg-red-200"
                                : "bg-green-100 text-green-800 hover:bg-green-200"
                            }`}
                          >
                            {selectedCategory.isActive ? "Deactivate" : "Activate"}
                          </button>
                          <button
                            onClick={() => handleDelete(selectedCategory.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {isEditing ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSave}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setIsEditing(false);
                              setIsCreating(false);
                              if (selectedCategory) {
                                setFormData(selectedCategory);
                              }
                            }}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <form onSubmit={(e) => e.preventDefault()}>
                      <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Name *
                            </label>
                            <input
                              type="text"
                              value={formData.name || ""}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter category name"
                              required
                            />
                          </div>
                          {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Icon (Emoji)
                            </label>
                            <input
                              type="text"
                              value={formData.icon || ""}
                              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="🏷️"
                            />
                          </div> */}
                        </div>

                        {/* <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            value={formData.description || ""}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter category description"
                          />
                        </div> */}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Color
                            </label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="color"
                                value={formData.color || "#3B82F6"}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                              />
                              <input
                                type="text"
                                value={formData.color || "#3B82F6"}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="#3B82F6"
                              />
                            </div> */}
                          {/* </div> */}
                          {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Sort Order
                            </label>
                            <input
                              type="number"
                              value={formData.sortOrder || 0}
                              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                          </div> */}
                        </div>

                        {/* Active Status */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="isActive"
                            checked={formData.isActive || false}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                            Active
                          </label>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-6">
                      {/* View Mode */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                            Basic Information
                          </h4>
                          <dl className="mt-2 space-y-2">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Name</dt>
                              <dd className="text-sm text-gray-900 flex items-center space-x-2">
                                {/* {selectedCategory?.icon && (
                                  <span className="text-lg">{selectedCategory.icon}</span>
                                )} */}
                                <span>{selectedCategory?.name}</span>
                              </dd>
                            </div>
                            {/* <div>
                              <dt className="text-sm font-medium text-gray-500">Slug</dt>
                              <dd className="text-sm text-gray-900">{selectedCategory?.slug}</dd>
                            </div> */}
                            {/* <div>
                              <dt className="text-sm font-medium text-gray-500">Description</dt>
                              <dd className="text-sm text-gray-900">
                                {selectedCategory?.description || "No description"}
                              </dd>
                            </div> */}
                            <div>
                              {/* <dt className="text-sm font-medium text-gray-500">Status</dt>
                              <dd>
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    selectedCategory?.isActive
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {selectedCategory?.isActive ? "Active" : "Inactive"}
                                </span>
                              </dd> */}
                            </div>
                          </dl>
                        </div>

                        <div>
                           <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                            Settings
                          </h4>
                          <dl className="mt-2 space-y-2">
                            {/* <div>
                              <dt className="text-sm font-medium text-gray-500">Color</dt>
                              <dd className="text-sm text-gray-900 flex items-center space-x-2">
                                <div
                                  className="w-4 h-4 rounded border border-gray-300"
                                  style={{ backgroundColor: selectedCategory?.color }}
                                ></div>
                                <span>{selectedCategory?.color}</span>
                              </dd>
                            </div> */}
                            {/* <div>
                              <dt className="text-sm font-medium text-gray-500">Sort Order</dt>
                              <dd className="text-sm text-gray-900">{selectedCategory?.sortOrder}</dd>
                            </div> */}
                            {/* <div>
                              <dt className="text-sm font-medium text-gray-500">Scripts Count</dt>
                              <dd className="text-sm text-gray-900">
                                {selectedCategory?._count?.scripts || 0} scripts
                              </dd>
                            </div> */}
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Created</dt>
                              <dd className="text-sm text-gray-900">
                                {selectedCategory?.createdAt
                                  ? new Date(selectedCategory.createdAt).toLocaleDateString()
                                  : "N/A"}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                              <dd className="text-sm text-gray-900">
                                {selectedCategory?.updatedAt
                                  ? new Date(selectedCategory.updatedAt).toLocaleDateString()
                                  : "N/A"}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      </div>

                      {/* Associated Scripts */}
                      {selectedCategory?.scripts && selectedCategory.scripts.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2">
                            Associated Scripts (Showing first 10)
                          </h4>
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Name
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Website
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Status
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Created
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {selectedCategory.scripts.map((script) => (
                                  <tr key={script.id}>
                                    <td className="px-4 py-2 text-sm text-gray-900">{script.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">
                                      {script.websiteUrl}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          script.isActive
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {script.isActive ? "Active" : "Inactive"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-500">
                                      {new Date(script.createdAt).toLocaleDateString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6 text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                    <svg
                      className="h-6 w-6 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No category selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select a category from the list to view details, or create a new one.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleCreateNew}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <svg
                        className="-ml-1 mr-2 h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Create New Category
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;